import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import type { AdminEvent } from '@hooks'
import { formatAdminDateTime, formatEventPrice } from '@utils'

/** One event line of the back-office: name, organizer, date and the figures that matter. */
export const AdminEventRow: React.FC<{ event: AdminEvent; onPress: () => void }> = ({
  event,
  onPress,
}) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const phaseColor =
    event.phase === 'ongoing'
      ? theme.colors.green
      : event.phase === 'upcoming'
        ? theme.colors.primary
        : theme.colors.greyBlue
  const paid = event.priceCents !== null

  return (
    <TouchableOpacity
      accessibilityRole="button"
      testID={`admin-event-${event.id}`}
      style={styles.row}
      onPress={onPress}
    >
      <View style={styles.body}>
        <View style={styles.titleLine}>
          <Text type="subtitleBold" numberOfLines={2} style={styles.title}>
            {event.name}
          </Text>
          <View style={[styles.phase, { borderColor: phaseColor }]}>
            <Text type="bodyExtraSmallBold" color={phaseColor}>
              {t(`admin.events.phase.${event.phase}`)}
            </Text>
          </View>
        </View>
        <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
          {[
            event.organizer?.name || event.organizer?.email || t('admin.events.noOrganizer'),
            formatAdminDateTime(event.startsAt, currentLanguage),
          ].join(' · ')}
        </Text>
        <View style={styles.figures}>
          <Figure
            label={t('admin.events.attendees')}
            value={String(event.attendeeCount)}
            testID={`admin-event-${event.id}-attendees`}
          />
          <Figure
            label={t('admin.events.checkedIn')}
            value={String(event.checkedInCount)}
            testID={`admin-event-${event.id}-checkedin`}
          />
          <Figure
            label={t('admin.events.price')}
            value={paid ? formatEventPrice(event.priceCents!, currentLanguage, event.currency) : t('admin.events.free')}
          />
          {paid ? (
            <Figure
              label={t('admin.events.paidTotal')}
              value={formatEventPrice(event.paidCents, currentLanguage, event.currency)}
              testID={`admin-event-${event.id}-paid`}
            />
          ) : null}
        </View>
      </View>
      <Icon name="arrowRight" size={18} color={theme.colors.greyBlue} />
    </TouchableOpacity>
  )
}

const Figure: React.FC<{ label: string; value: string; testID?: string }> = ({
  label,
  value,
  testID,
}) => {
  const theme = useTheme()
  return (
    <View style={{ minWidth: 64 }}>
      <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
        {label}
      </Text>
      <Text type="bodySmallBold" testID={testID}>
        {value}
      </Text>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    body: { flex: 1, minWidth: 0, gap: theme.spacing.xs },
    titleLine: { flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.sm },
    title: { flex: 1 },
    phase: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: 2,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
    },
    figures: { flexDirection: 'row', flexWrap: 'wrap', columnGap: theme.spacing.lg, rowGap: theme.spacing.xs },
  })
